import React, { useState, useEffect, ChangeEvent, MouseEvent } from 'react';
import {
	Card,
	CardHeader,
	CardContent,
	Grid,
	FormGroup,
	InputLabel,
	RadioGroup,
	FormControlLabel,
	Radio,
	Select,
	MenuItem,
	FormHelperText,
	CircularProgress,
	Button,
	Typography,
	makeStyles
} from '@material-ui/core';
import { useQuery } from '@apollo/react-hooks';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import DayjsUtils from '@date-io/dayjs';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import { getGameHighlights } from '../../games/apiQueries';
import { getAiringHighlights } from '../../airings/apiQueries';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Highlight } from '../../../utils/types';
import { getCurrentDateString } from '../../../utils/dateTransform';
import { usePrevious } from '../../../hooks';

dayjs.extend(localizedFormat);

const POLL_INTERVAL = 15000;

const sortHightlightByPublishTime = (highlight1: Highlight, highlight2: Highlight): number => {
	if (highlight1.publishTime! > highlight2.publishTime!) {
		return -1;
	}
	if (highlight1.publishTime! < highlight2.publishTime!) {
		return 1;
	}
	return 0;
};

const useStyles = makeStyles((theme) => ({
	formGroup: {
		flexWrap: 'nowrap'
	},
	formHelperText: {
		marginBottom: theme.spacing(2)
	},
	loadMoreContainer: {
		paddingTop: theme.spacing(1.5)
	},
	loadMoreButton: {
		marginLeft: theme.spacing(2)
	}
}));

const HighlightSelector: React.FC<HighlightSelectorProps> = ({ id, mode, onSelect }) => {
	const classes = useStyles();
	const todaysDate = getCurrentDateString();

	const [selectedType, setSelectedType] = useState<string>('ALL');
	const [selectedPublishDate, setSelectedPublishDate] = useState<string>(todaysDate);
	const [selectedHighlightId, setSelectedHighlightId] = useState<string>('');

	const [highlights, setHighlights] = useState<Highlight[] | []>([]);
	const previousHighlights = usePrevious(highlights);
	const [nextToken, setNextToken] = useState<string | null>(null);

	const [selectableHighlights, setSelectableHighlights] = useState<Highlight[] | []>([]);

	const query = mode === 'airing' ? getAiringHighlights : getGameHighlights;
	const queryResultPath = mode === 'airing' ? 'getAiring' : 'getGame';

	const { loading, error, data } = useQuery(query, {
		variables: {
			nextToken,
			id: decodeURIComponent(id),
			available: true,
			publishTime: selectedPublishDate
		},
		pollInterval: POLL_INTERVAL,
		notifyOnNetworkStatusChange: true
	});

	useEffect(() => {
		// reset selected highlight, when selection is no longer in highlights list
		if (selectedHighlightId !== '' && highlights.length > 0 && !highlights.find((highlight) => highlight.id === selectedHighlightId)) {
			setSelectedHighlightId('');
		}
	}, [selectedHighlightId, highlights]);

	useEffect(() => {
		// reset highlights when the selected publishDate filter changes
		setHighlights([]);
		setNextToken(null);
	}, [selectedPublishDate]);

	useEffect(() => {
		// merge existing and newly retrieved highlights
		const dataHighlights = data?.[queryResultPath]?.highlights?.items;
		if (dataHighlights && previousHighlights) {
			let merged: Highlight[] = [...highlights, ...dataHighlights];

			merged = merged.filter((highlight, index, self) => {
				return index === self.findIndex((other) => other.id === highlight.id);
			});

			merged.sort(sortHightlightByPublishTime);
			if (!isEqual(merged, highlights)) {
				setHighlights(merged);
			}
		}
	}, [data, highlights, previousHighlights, queryResultPath]);

	useEffect(() => {
		let result: Highlight[] = [...highlights];

		// filter out by type
		if (selectedType !== 'ALL') {
			result = result.filter((highlight) => highlight.type === selectedType);
		}

		// sort
		result.sort(sortHightlightByPublishTime);

		setSelectableHighlights(result);
		if (!selectedHighlightId && result?.length > 0) {
			for (const highlight of result) {
				if (highlight.type === 'DRAFT') {
					setSelectedHighlightId(highlight.id);
					onSelect(highlight);
					return;
				} else {
					setSelectedHighlightId(result[0].id);
					onSelect(result[0]);
				}
			}
		}
	}, [highlights, selectedType, onSelect, selectedHighlightId]);

	const handleSelect = (highlightId: unknown): void => {
		setSelectedHighlightId(highlightId as string);
		const selectedHighlight = selectableHighlights.find((highlight) => highlight.id === highlightId);
		if (selectedHighlight) onSelect(selectedHighlight);
	};

	const handleTypeChange = (event: ChangeEvent<{ value: unknown }>): void => {
		const { value } = event.target;
		value && setSelectedType(value as string);
	};

	const handlePublishDateChange = (date: MaterialUiPickersDate): void => {
		setSelectedPublishDate(
			dayjs(date ?? '')
				.utc()
				.local()
				.format('YYYY-MM-DD') ?? null
		);
	};

	const loadMoreHighlights = (e: MouseEvent): void => {
		e.preventDefault();
		data?.getGame?.highlights?.nextToken && setNextToken(data?.getGame?.highlights?.nextToken);
	};

	const timeNow = dayjs().format('HH:mm:ss');

	return (
		// prettier-ignore
		<Card>
            <CardHeader title="Select a Source Highlight" />
            <CardContent>
                <Grid container>
                    <Grid item xs={8}>
                        <Grid container>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={12} sm={6}>
                                        <InputLabel id="selectedHighlightType">
                                            Highlight Type
                                        </InputLabel>
                                        <RadioGroup
                                            row
                                            aria-label="Highlight Type"
                                            name="selectedHighlightType"
                                            value={selectedType || ''}
                                            onChange={handleTypeChange}
                                        >
                                            <FormControlLabel
                                                value="ALL"
                                                control={<Radio color="primary" />}
                                                label="All"
                                            />
                                            <FormControlLabel
                                                value="DRAFT"
                                                control={<Radio color="primary" />}
                                                label="Draft"
                                            />
                                            <FormControlLabel
                                                value="LIVE"
                                                control={<Radio color="primary" />}
                                                label="Live"
                                            />
                                        </RadioGroup>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MuiPickersUtilsProvider
                                            utils={DayjsUtils}
                                        >
                                            <DatePicker
                                                data-testid="highlight-selector-publish-date-picker"
                                                disableToolbar
                                                autoOk
                                                variant="inline"
                                                format="DD-MM-YYYY"
                                                label="Publish Date"
                                                value={selectedPublishDate}
                                                onChange={handlePublishDateChange}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                {error && (
                                    <Typography>
                                        Error loading data from the server
                                    </Typography>
                                )}
                                {!loading &&
                                    !error &&
                                    selectableHighlights.length === 0 &&
                                    (
                                        <Typography>
                                            No Highlights are available for these filters
                                        </Typography>
                                    )}
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={8}>
                                        <FormGroup>
                                            {!error && selectableHighlights.length > 0 && (
                                                <>
                                                    <InputLabel id="selectedHighlightIdLabel">
                                                        Source highlight
                                                    </InputLabel>
                                                    <Select
                                                        data-testid="highlight-selector-selected-highlight"
                                                        name="selectedHighlightId"
                                                        displayEmpty
                                                        value={selectedHighlightId}
                                                        onChange={(
                                                            event: ChangeEvent<{ value: unknown }>
                                                        ): void => handleSelect(event.target.value)}
                                                    >
                                                        <MenuItem
                                                            key="highlightEmptyOption"
                                                            value=""
                                                            disabled
                                                        >
                                                            <em>Select a Highlight</em>
                                                        </MenuItem>
                                                        {(selectableHighlights as Highlight[]).map(
                                                            (
                                                                highlight: Highlight
                                                            ) => (
                                                                <MenuItem
                                                                    key={highlight.id}
                                                                    value={highlight.id}
                                                                >
                                                                    {dayjs(highlight.publishTime ?? '').format('lll')}{' '}
                                                                    -{' '}
                                                                    {highlight.periods?.items?.length || 0}{' '}
                                                                    periods{' '}
                                                                    ({highlight.type}:{' '}
                                                                    {highlight.available ? 'Available' : 'Unavailable'})
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Select>
                                                    <FormHelperText
                                                        className={classes.formHelperText}
                                                    >
                                                        list refreshes every{' '}
                                                        {POLL_INTERVAL / 1000}{' '}
                                                        secs - last updated at {timeNow}
                                                    </FormHelperText>
                                                </>
                                            )}
                                        </FormGroup>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Grid container spacing={2} className={classes.loadMoreContainer}>
                                            {!error &&
                                                selectableHighlights.length > 0 &&
                                                (
                                                    <>
                                                        {data?.getGame?.highlights?.nextToken && (
                                                            <Grid item>
                                                                <Button
                                                                    className={classes.loadMoreButton}
                                                                    variant="contained"
                                                                    color="primary"
                                                                    onClick={loadMoreHighlights}
                                                                >
                                                                    Load More{' '}
                                                                </Button>
                                                            </Grid>
                                                        )}
                                                        {loading && (
                                                            <Grid item>
                                                                <CircularProgress size={30} />
                                                            </Grid>
                                                        )}
                                                    </>
                                                )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
	);
};

type HighlightSelectorProps = {
	id: string;
	mode: 'game' | 'airing';
	onSelect: (highlight: Highlight) => void;
};

export default HighlightSelector;
